import { AfterViewInit, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as L from 'leaflet';
import { icon } from 'leaflet';
import * as turf from '@turf/turf';
import 'leaflet-editable';
import 'leaflet-contextmenu';
import 'leaflet-path-transform';
import { StudyShapeGenerator } from './study-shape-generator';
import { convertPolygonToTurf } from './shape/shape-utils';
import { FieldMapContext } from './field-map-context.model';
import { FieldMapShape } from './shape/field-map-shape';
import { Field } from './shape/field';
import { Block } from './shape/block';
import { Study } from './shape/study';
import { WorkbenchLocationService } from '../shared/workbench-location/service/workbench-location.service';
import { LocationTypeEnum } from '../shared/workbench-location/model/location-type.enum';
import { LocationItem, WorkbenchLocationListComponent } from './workbench-location-list.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocationRequest } from '../shared/workbench-location/model/location-request.model';
import { ModalConfirmComponent } from '../shared/modal/modal-confirm.component';
import { ObservationUnitsService } from './service/observationUnits.service';
import { Plot } from './shape/plot';
import { MatSidenav } from '@angular/material/sidenav';
import { StudyInstancesViewComponent } from './study-instances-view/study-instances-view.component';
import { LocationStudyService } from '../shared/workbench-location/service/location-study.service';
import { FieldmapInfo, FieldmapService } from './service/fieldmap.service';
import { ModalInputComponent } from './modal/modal-input.component';
import { customCatchError } from './utils';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../shared/alert/alert.service';
import { HelpService } from '../shared/service/help.service';
import { HELP_FIELDMAP_MANAGER } from '../app.constants';
import { JhiLanguageService } from '../shared/core-utils';

@Component({
    selector: 'jhi-field-management-map',
    templateUrl: './field-management-map.component.html',
    styleUrls: ['./field-management-map.component.css']
})
export class FieldManagementMapComponent implements AfterViewInit, OnInit {

    @ViewChild('sideNavRight') leftSideNav!: MatSidenav;
    @ViewChild('sideNavLeft') rightSideNav!: MatSidenav;

    leftSidenavOpen = true;
    rightSidenavOpen = true;

    public readonly LocationTypeEnum = LocationTypeEnum;

    map: L.Map | undefined;
    searchQuery = '';
    selectedFieldFieldMapShape: FieldMapShape | null = null;
    drawnFields: L.FeatureGroup = L.featureGroup();
    drawnPolygon: L.Polygon | undefined;

    selectedField: number;
    selectedBlock: number;

    plotHover: FieldMapShape;

    snackBarMessage;
    snackBarType: 'ERROR' | 'INFO' | 'WARNING';

    isLoading = false;
    loadingMessage: string;

    helpLink: string;

    protected readonly Plot = Plot;

    @ViewChild('fieldList') fieldList!: WorkbenchLocationListComponent;
    @ViewChild('blockList') blockList!: WorkbenchLocationListComponent;
    @ViewChild('studyInstancesView') studyInstancesView!: StudyInstancesViewComponent;

    constructor(private http: HttpClient, private studyMapGenerator: StudyShapeGenerator,
                private workbenchLocationService: WorkbenchLocationService,
                private observationUnitsService: ObservationUnitsService,
                private locationStudyService: LocationStudyService,
                public fieldMapContext: FieldMapContext,
                private fieldmapService: FieldmapService,
                private modalService: NgbModal,
                public jhiLanguageService: JhiLanguageService,
                public translateService: TranslateService,
                public alertService: AlertService,
                private helpService: HelpService) {
    }

    ngOnInit(): void {
        this.helpService.getHelpLink(HELP_FIELDMAP_MANAGER).toPromise().then((response) => {
            if (response.body) {
                this.helpLink = response.body;
            }
        });
    }

    ngAfterViewInit(): void {
        this.initMap();
        this.setupSidenavListeners();
    }

    private initMap(): void {
        this.map = new L.Map('map', {
            editable: true,
            zoomControl: false  // Disable default zoom control
        }).setView([51.505, -0.09], 13);
        L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?blankTile=false', {
            attribution: '&copy; <a href="http://www.esri.com/">Esri</a>, ' +
                'DigitalGlobe, GeoEye, i-cubed, USDA FSA, USGS, AEX, Getmapping, Aerogrid, IGN,' +
                ' IGP, swisstopo, and the GIS User Community',
            maxZoom: 28,
            maxNativeZoom: 19
        }).addTo(this.map);
        this.drawnFields.addTo(this.map);

        // Add custom positioned zoom control
        L.control.zoom({
            position: 'topright',  // Position the zoom controls on the top right
            // Optional custom zoom control styling
            zoomInText: '+',
            zoomOutText: '-',
            zoomInTitle: 'Zoom in',
            zoomOutTitle: 'Zoom out'
        }).addTo(this.map);

        // Add resize observer to handle any parent size changes
        const mapElement = document.getElementById('map');
        if (mapElement) {
            // @ts-ignore
            const resizeObserver = new ResizeObserver(() => {
                if (this.map) {
                    // Add a small delay to ensure DOM is updated
                    setTimeout(() => {
                        this.map.invalidateSize();
                    }, 100);
                }
            });

            resizeObserver.observe(mapElement);
        }

        this.map.on('editable:drawing:end', (e: L.LeafletEvent) => {
            // Remove tooltip on drawing end.
            this.hideSnackBar();
            this.map.off('mousemove');
        });

        this.map.on('editable:drawing:click', (e: L.CancelableEvent) => {
            if (this.fieldMapContext.drawingMode === 'BLOCK') {
                const turfBoundingPolygon = convertPolygonToTurf(this.selectedFieldFieldMapShape.polygon);
                const clickLatLng = (e as any).latlng as L.LatLng;
                // During drawing of Block, if the user clicks outside of the selected Field,
                // cencel the click event to prevent Leaflet to create a new Vertex
                if (!this.isPointInPolygon(clickLatLng, turfBoundingPolygon)) {
                    // Click outside bounding area. Canceling click
                    e.cancel();
                    return;
                }
                if (e.layer.editor._drawnLatLngs.length < 3) {
                    this.showSnackBar('INFO', 'field-manager.drawing.block.continue');
                } else {
                    this.showSnackBar('INFO', 'field-manager.drawing.block.finish');
                }
            } else if (this.fieldMapContext.drawingMode === 'STUDY') {
                const turfBoundingPolygon = convertPolygonToTurf(this.selectedFieldFieldMapShape.polygon);
                const clickLatLng = (e as any).latlng as L.LatLng;
                // During drawing of Block, if the user clicks outside of the selected Field,
                // cencel the click event to prevent Leaflet to create a new Vertex
                if (!this.isPointInPolygon(clickLatLng, turfBoundingPolygon)) {
                    // Click outside bounding area. Canceling click
                    e.cancel();
                    return;
                }
                if (e.layer.editor._drawnLatLngs.length < 3) {
                    this.showSnackBar('INFO', 'field-manager.drawing.study.continue');
                } else {
                    this.showSnackBar('INFO', 'field-manager.drawing.study.finish');
                }
            } else if (this.fieldMapContext.drawingMode === 'FIELD') {
                if (e.layer.editor._drawnLatLngs.length < 3) {
                    this.showSnackBar('INFO', 'field-manager.drawing.field.continue');
                } else {
                    this.showSnackBar('INFO', 'field-manager.drawing.field.finish');
                }
            }

        });

        this.map.on('editable:vertex:new', (e: L.VertexEvent) => {
            // For drawing blocks
            if (this.fieldMapContext.drawingMode === 'BLOCK' || this.fieldMapContext.drawingMode === 'STUDY') {
                const turfBoundingPolygon = convertPolygonToTurf(this.selectedFieldFieldMapShape.polygon);
                const newLatLng = e.vertex.getLatLng();
                if (!this.isPointInPolygon(newLatLng, turfBoundingPolygon)) {
                    // Point outside bounding area. Deleting...
                    e.vertex.delete();
                    e.vertex.getPrevious().continue();
                }
            }
        });

        this.map.on('editable:vertex:dragstart', (e: any) => {
            if (this.selectedFieldFieldMapShape.type === 'BLOCK') {
                const block = this.selectedFieldFieldMapShape as Block;
                const originalLatLng = e.vertex.latlng.clone();
                const turfBoundingPolygon = convertPolygonToTurf(block.parentField.polygon);

                const onDragEnd = () => {
                    const newLatLng = e.vertex.latlng;

                    if (!this.isPointInPolygon(newLatLng, turfBoundingPolygon)) {
                        // Point outside bounding area. Reverting.
                        e.vertex.latlng.lat = originalLatLng.lat;
                        e.vertex.latlng.lng = originalLatLng.lng;
                        (this.drawnPolygon as any).editor.reset();
                        this.drawnPolygon.redraw();
                        block.parentField.updateStyle('DEFAULT');
                    }
                };

                const onDrag = () => {
                    const newLatLng = e.vertex.latlng;
                    if (!this.isPointInPolygon(newLatLng, turfBoundingPolygon)) {
                        // Point outside bounding area
                        block.parentField.updateStyle('BOUNDARY_ERROR');
                    } else {
                        block.parentField.updateStyle('DEFAULT');
                    }
                };

                e.vertex.on('drag', onDrag);
                e.vertex.once('dragend', onDragEnd);

            }
        });

        // FIXME: Workaround
        (L.Handler['PathTransform'] as any).prototype._getBoundingPolygon = function() {
            return new L.Rectangle(this._path.getBounds(), this.options.boundsOptions);
        };

        this.fieldMapContext.onShapeClicked = (shape: FieldMapShape) => {
            // No implementation
        };

        this.fieldMapContext.onShapeSelected = (shape: FieldMapShape) => {
            // Unselect the previously selected shape.
            if (this.selectedFieldFieldMapShape && this.selectedFieldFieldMapShape.id !== shape.id) {
                this.selectedFieldFieldMapShape.unSelect();
            }
            this.selectedFieldFieldMapShape = shape;
            this.fieldMapContext.editOptionsSelectedValue = null;

            if (shape.type === 'STUDY') {
                const study = shape as Study;
                if (study.parentBlock === null) {
                    this.showSnackBar('WARNING', 'field-manager.drawing.study.no-block');
                }
            }
        };

        this.fieldMapContext.shapeUnSelected = (shape: FieldMapShape) => {
            this.selectedFieldFieldMapShape = null;
            this.fieldMapContext.editOptionsSelectedValue = null;
            this.hideSnackBar();
        };

        this.fieldMapContext.onBeforeShapeCommit = async(shape: FieldMapShape) => {
            // Before committing FIELD or BLOCK shape, test first if it can be saved.
            // If there's a validation error, reject the promise to avoid committing the changes.
            if (shape.type === 'FIELD' || shape.type === 'BLOCK') {
                const location = await this.workbenchLocationService.getLocationById(shape.id).toPromise();
                const locationRequest = LocationRequest.fromLocation(location);
                locationRequest.geometry = JSON.stringify(shape.polygon.toGeoJSON());
                locationRequest.name = shape.name;

                try {
                    await this.workbenchLocationService.updateLocation(locationRequest, parseInt(shape.id, 10)).toPromise();
                } catch (errorResponse) {
                    this.alertService.error('field-manager.errors.generic.operation-failed');
                    return Promise.reject(errorResponse);
                }
            }
        };

        this.fieldMapContext.afterShapeCommit = async(shape: FieldMapShape) => {

            if (shape.type === 'STUDY') {

                this.startThrobber('Please hold on, saving is in progress...');

                try {
                    const study = shape as Study;
                    const bodyParams = [];

                    study.plotsDrawingLayer.eachLayer((plot) => {
                        const plotPolygon = plot as L.Polygon;
                        const observationUnitDbId = plotPolygon.feature.properties.observationUnit.observationUnitDbId;
                        const geoJson = plotPolygon.toGeoJSON();
                        geoJson.properties = {};
                        const patchParams = {
                            observationUnitPosition: {
                                geoCoordinates: geoJson,
                                observationLevel: { levelName: 'PLOT' }
                            },
                            observationUnitDbId
                        };
                        bodyParams.push(patchParams);

                    });

                    await this.observationUnitsService.observationunitsPatch(study.crop, bodyParams).toPromise();

                    if (study.parentBlock) {
                        // Create a record that links this study to a block.
                        const existingLocationStudy = await this.locationStudyService.getByGeolocation(parseInt(study.id, 10)).toPromise();
                        if (!existingLocationStudy) {
                            await this.locationStudyService.createStudyLocation({
                                cropName: study.crop,
                                locId: parseInt(study.parentBlock.id, 10),
                                ndGeolocationId: parseInt(study.id, 10)
                            }).toPromise();
                        }
                    }

                    // Reload the study instances view
                    this.studyInstancesView.refreshAll();

                    this.stopThrobber();

                    this.alertService.success('field-manager.success.study.save', null, 5000);
                } catch (errorResponse) {
                    this.stopThrobber();
                    this.alertService.error('field-manager.errors.generic.update-failed');
                    return Promise.reject(errorResponse);
                }

            } else if (shape.type === 'PLOT') {
                const plot = shape as Plot;
                const plotPolygon = plot.polygon;
                const observationUnitDbId = plotPolygon.feature.properties.observationUnit.observationUnitDbId;
                const geoJson = plotPolygon.toGeoJSON();
                geoJson.properties = {};
                const patchParams = {
                    observationUnitPosition: {
                        geoCoordinates: geoJson,
                        observationLevel: { levelName: 'PLOT' }
                    },
                    observationUnitDbId: plotPolygon.feature.properties.observationUnit.observationUnitDbId
                };
                try {
                    this.observationUnitsService.observationunitPatch(plot.crop, observationUnitDbId, patchParams).toPromise();
                    this.alertService.success('field-manager.success.plot.save', null, 5000);
                } catch (errorResponse) {
                    return Promise.reject(errorResponse);
                }

            } else {
                const location = await this.workbenchLocationService.getLocationById(shape.id).toPromise();
                const locationRequest = LocationRequest.fromLocation(location);
                locationRequest.geometry = JSON.stringify(shape.polygon.toGeoJSON());
                locationRequest.name = shape.name;

                try {
                    await this.workbenchLocationService.updateLocation(locationRequest, parseInt(shape.id, 10)).toPromise();
                } catch (errorResponse) {
                    this.alertService.error('field-manager.errors.generic.update-failed');
                    return Promise.reject(errorResponse);
                }
            }
        };

        this.fieldMapContext.onBeforeDelete = async(shape: FieldMapShape): Promise<any> => {

            // Open confirmation modal and await user response
            const modalRef = this.modalService.open(ModalConfirmComponent);
            modalRef.componentInstance.title = this.translateService.instant('field-manager.modal.delete-confirmation.title');
            modalRef.componentInstance.message = this.translateService.instant('field-manager.modal.delete-confirmation.message', {
                param1: shape.name,
                param2: shape.type.toLowerCase()
            });

            // Wait for modal result
            const [error] = await customCatchError(modalRef.result);

            // If there's an error, the modal is dismissed/canceled.
            // In this case immediately reject the promise.
            if (error) {
                return Promise.reject('dismiss');
            }

            try {

                if (shape.type === 'BLOCK') {
                    // Delete location
                    await this.workbenchLocationService.deleteLocation(parseInt(shape.id, 10)).toPromise();
                    // Refresh appropriate list based on shape type
                    this.blockList.refresh();
                } else if (shape.type === 'FIELD') {
                    // Delete location
                    await this.workbenchLocationService.deleteLocation(parseInt(shape.id, 10)).toPromise();
                    // Refresh appropriate list based on shape type
                    this.fieldList.refresh();
                } else if (shape.type === 'STUDY') {
                    const study = shape as Study;
                    // Delete database record that associates study to a block.
                    if (study.parentBlock) {
                        await this.locationStudyService.deleteByLocationAndGeolocation(parseInt(study.parentBlock.id, 10), parseInt(study.id, 10)).toPromise();
                    }
                    // Delete study geoJson data
                    // TODO: Where to get the crop name???
                    await this.fieldmapService.deleteGeoreferences(study.crop, study.id).toPromise();
                    this.studyInstancesView.refreshAll();
                }

                this.selectedFieldFieldMapShape = null;

                return 'OK';
            } catch (error) {
                this.alertService.error('field-manager.errors.generic.delete-failed', null, 5000);
                console.error(error);
                return Promise.reject('field-manager.errors.generic.delete-failed');
            }
        };

        this.fieldMapContext.onShapeTransforming = (data, shape) => {
            if (shape.type === 'BLOCK') {
                if (data.isOutOfBoundary) {
                    this.showSnackBar('ERROR', 'field-manager.boundary.block');
                } else {
                    this.hideSnackBar();
                }
            } else if (shape.type === 'STUDY') {
                if (data.isOutOfBoundary) {
                    this.showSnackBar('ERROR', 'field-manager.boundary.study');
                } else {
                    this.hideSnackBar();
                }
            }
        };

        this.fieldMapContext.onShapeMouseOver = (shape) => {
            if (shape.type === 'PLOT') {
                this.plotHover = shape;
            }
        };

        this.fieldMapContext.onShapeMouseOut = (shape) => {
            if (shape.type === 'PLOT') {
                this.plotHover = null;
            }
        };
    }

    saveToolAction() {
        if (this.selectedFieldFieldMapShape && this.selectedFieldFieldMapShape.isEditing) {
            // TODO: Do the update here.
            this.selectedFieldFieldMapShape.commit().then(() => {
                this.selectedFieldFieldMapShape.unSelect();
                this.fieldMapContext.editOptionsSelectedValue = null;
            });
        } else {
            this.commitDrawing();
        }
    }

    editToolAction() {
        if (this.fieldMapContext.editOptionsSelectedValue === 'EDIT') {
            this.selectedFieldFieldMapShape.edit();
        } else if (this.fieldMapContext.editOptionsSelectedValue === 'RESIZE') {
            this.selectedFieldFieldMapShape.resize();
        } else if (this.fieldMapContext.editOptionsSelectedValue === 'ROTATE') {
            this.selectedFieldFieldMapShape.rotate();
        }
    }

    cancelToolAction() {
        if (this.selectedFieldFieldMapShape && this.selectedFieldFieldMapShape.isEditing) {
            this.selectedFieldFieldMapShape.reset();
            this.selectedFieldFieldMapShape.select();
            // this.cancelSelect();
            this.fieldMapContext.editOptionsSelectedValue = null;
        } else {
            if (this.map.editTools.drawing()) {
                this.cancelDrawing();
            }
            if (this.selectedFieldFieldMapShape) {
                this.selectedFieldFieldMapShape.select();
            }
        }
    }

    isSaveEnabled() {
        if (this.map) {
            if (this.map.editTools.drawing()) {
                return true;
            }
            if (this.selectedFieldFieldMapShape && this.selectedFieldFieldMapShape.isSaveable()) {
                return true;
            }
        }
        return false;
    }

    private isDrawing() {
        if (this.map) {
            return this.map.editTools.drawing();
        }
        return false;
    }

    showSave() {
        // Show the Save button if
        // 1. Map is in drawing state
        // 2. A shape is currently selected
        return this.isDrawing() || this.isSelected();
    }

    disableCreateAction() {
        // Disable the Create Action if
        // 1. Map is in drawing state
        // 2. If the selected shape extends outside its parent boundary
        return this.isDrawing() || (this.selectedFieldFieldMapShape && this.selectedFieldFieldMapShape.isOutsideBoundary) || !this.fieldMapContext.mouseEventListenerEnabled;
    }

    isSelected(type?: string) {
        if (type) {
            return (this.selectedFieldFieldMapShape && this.selectedFieldFieldMapShape.isSelected && this.selectedFieldFieldMapShape.type === type);
        }
        return (this.selectedFieldFieldMapShape && this.selectedFieldFieldMapShape.isSelected);
    }

    private isPointInPolygon(point: L.LatLng, turfBoundingPolygon: any): boolean {
        const turfPoint = turf.point([point.lng, point.lat]);
        return turf.booleanPointInPolygon(turfPoint, turfBoundingPolygon);
    }

    onLocationSelected(location: { lat: number, lon: number, displayName: string }): void {
        this.map.setView([location.lat, location.lon], 13);
        L.marker([location.lat, location.lon], {
            icon: icon({
                iconSize: [25, 41],
                iconAnchor: [13, 41],
                iconUrl: '/ibpworkbench/main/app/content/scss/images/marker-icon.png',
                shadowUrl: '/ibpworkbench/main/app/content/scss/images/marker-shadow.png'
            })
        }).addTo(this.map)
            .bindPopup(location.displayName)
            .openPopup();
    }

    startCreateField() {
        this.fieldMapContext.drawingMode = 'FIELD';
        this.cancelAll();
        this.showSnackBar('INFO', 'field-manager.drawing.field.start');
        this.drawnPolygon = this.map.editTools.startPolygon();
        this.map.once('editable:drawing:commit', (event: L.LeafletEvent) => {
            const layer = event.layer;
            if (layer instanceof L.Polygon) {
                if (this.isPolygonComplete(layer)) {
                    this.addField(layer);
                    this.fieldMapContext.drawingMode = null;
                } else {
                    this.alertService.error('field-manager.errors.generic.polygon-incomplete');
                    this.cancelDrawing();
                    this.fieldMapContext.drawingMode = null;
                }
            }
        });
    }

    startCreateBlock() {
        this.fieldMapContext.drawingMode = 'BLOCK';

        let parentField: Field;
        if (this.selectedFieldFieldMapShape.type === 'BLOCK') {
            // When creating a block, if the current selected field is a block,
            // we should automatically select it's parent field.
            this.selectedFieldFieldMapShape.reset();
            const block = (this.selectedFieldFieldMapShape as Block);
            block.unSelect();
            this.selectedFieldFieldMapShape = block.parentField;
            parentField = this.selectedFieldFieldMapShape as Field;
            parentField.select();
            parentField.fitView();
        } else {
            parentField = this.selectedFieldFieldMapShape as Field;
        }

        // When creating a new block, cancel all editing/transformation actions and deselect the current selection.
        this.selectedFieldFieldMapShape.reset();

        // Update the Field style to visualize boundary limit.
        this.selectedFieldFieldMapShape.updateStyle('BOUNDARY');

        this.showSnackBar('INFO', 'field-manager.drawing.block.start');
        this.drawnPolygon = this.map.editTools.startPolygon();

        this.map.once('editable:drawing:commit', (event: L.LeafletEvent) => {
            const layer = event.layer;

            if (layer instanceof L.Polygon) {
                if (this.isPolygonComplete(layer)) {
                    this.addBlock(layer, parentField);
                    this.fieldMapContext.drawingMode = null;
                } else {
                    this.alertService.error('field-manager.errors.generic.polygon-incomplete');
                    this.cancelDrawing();
                    this.selectedFieldFieldMapShape.select();
                    this.fieldMapContext.drawingMode = null;
                }
            }
        });
    }

    startCreateStudy(fielmapInfo: FieldmapInfo) {
        this.fieldMapContext.drawingMode = 'STUDY';
        if (this.selectedFieldFieldMapShape && this.selectedFieldFieldMapShape.type === 'BLOCK') {
            const parentField = this.selectedFieldFieldMapShape;

            // When creating a new study, cancel all editing/transformation actions and deselect the current selection.
            this.selectedFieldFieldMapShape.reset();

            // Update the Block style to visualize boundary limit.
            this.selectedFieldFieldMapShape.updateStyle('BOUNDARY');

            this.showSnackBar('INFO', 'field-manager.drawing.study.start');
            this.drawnPolygon = this.map.editTools.startPolygon();

            this.map.once('editable:drawing:commit', (event: L.LeafletEvent) => {
                const layer = event.layer;
                if (layer instanceof L.Polygon) {
                    if (this.isPolygonComplete(layer)) {
                        this.addStudy(fielmapInfo, layer, parentField as Block);
                    } else {
                        this.alertService.error('field-manager.errors.generic.polygon-incomplete');
                        this.cancelDrawing();
                    }
                }
                this.fieldMapContext.editOptionsSelectedValue = null;
                this.selectedFieldFieldMapShape.updateStyle('SELECTED');
                this.selectedFieldFieldMapShape.reset();
                this.fieldMapContext.drawingMode = null;
            });
        } else {
            this.alertService.error('field-manager.selection.block-required', null, 5000);
        }
    }

    private addField(layer: L.Polygon): void {

        const modalRef = this.modalService.open(ModalInputComponent);
        modalRef.componentInstance.title = this.translateService.instant('field-manager.modal.field-name.title');
        modalRef.result.then((result: { name: string, optionalLocationId: number }) => {
            if (result.name) {
                this.studyMapGenerator.generateField(result.name, layer, this.map, this.drawnFields).then((field: Field) => {
                    this.fieldList.refresh();
                    this.selectedField = parseInt(field.id, 10);
                }, () => {
                    this.alertService.error('field-manager.errors.generic.create-failed', null, 5000);
                    layer.remove();
                    this.fieldMapContext.drawingMode = null;
                });
            }
        }, () => {
            layer.remove();
            this.fieldMapContext.drawingMode = null;
        });
    }

    private addBlock(layer: L.Polygon, parentField: Field): void {
        const modalRef = this.modalService.open(ModalInputComponent);
        modalRef.componentInstance.title = this.translateService.instant('field-manager.modal.block-name.title');
        modalRef.result.then((result: { name: string, optionalLocationId: number }) => {
            if (result.name) {
                this.studyMapGenerator.generateBlock(result.name, layer, this.map, parentField).then(() => {
                    this.blockList.refresh();
                }, () => {
                    this.alertService.error('field-manager.errors.generic.create-failed', null, 5000);
                    layer.remove();
                    this.fieldMapContext.drawingMode = null;
                });
            }
        }, () => {
            layer.remove();
            this.fieldMapContext.drawingMode = null;
        });
    }

    addStudy(fieldmapInfo: FieldmapInfo, layer: L.Polygon, block: Block) {
        this.studyMapGenerator.generateStudyShape(fieldmapInfo.crop, this.map, block, fieldmapInfo.instanceId.toString(), layer, this.fieldMapContext).then(async(study: Study) => {
            // When study.draw() is invoked, it will also invoke draw() method of the plots associated to study.
            study.draw();
            study.reset();
            study.select();

            // Once the study is drawn, detect if it is outside the boundary of its parent field.
            // If the study is outside the boundary, automatic enable resize mode
            // so that the user can adjust it manually.
            study.detectOutsideBoundary();

            if (!study.isOutsideBoundary) {
                // If the generated study/plots is not outside the field boundary
                // Invoke commit to save the study/plots data when study is drawn
                study.commit();
            }
        }, () => {

            this.alertService.error('field-manager.errors.generic.create-failed', null, 5000);
            // Study is already present inside the block
            this.cancelDrawing();

        });
    }

    /**
     * Cancels all ongoing actions related to drawing, editing, and selecting.
     *
     * @return {void}
     */
    cancelAll() {
        if (this.selectedFieldFieldMapShape) {
            this.selectedFieldFieldMapShape.reset();
            this.selectedFieldFieldMapShape.unSelect();
        }
    }

    /**
     * Cancels the current drawing operation on the map.
     *
     * @return {void}
     */
    cancelDrawing(): void {
        this.map.editTools.stopDrawing();
        this.map.removeLayer(this.drawnPolygon);
        this.drawnPolygon = null;
        this.fieldMapContext.editOptionsSelectedValue = null;
        // Remove the drawing commit event listener
        this.map.off('editable:drawing:commit');
    }

    /**
     * Commits the current drawing in the map edit tools.
     *
     * @return {void}
     */
    commitDrawing(): void {
        this.map.editTools.commitDrawing(null);
    }

    onFieldItemSelect(locationItemId: number): Promise<FieldMapShape> {
        return new Promise(async(resolve) => {
            if (!this.fieldMapContext.fieldsMap.has(locationItemId.toString())) {
                const location = await this.workbenchLocationService.getLocationById(locationItemId.toString()).toPromise();

                if (location && location.geometry) {
                    this.studyMapGenerator.loadField(location, this.map, this.drawnFields).then((field) => {
                        field.fitView();
                        this.selectedField = locationItemId;
                        resolve(field);
                    });
                } else {
                    this.alertService.error('field-manager.errors.field.load-failed', null, 5000);
                    resolve(null);
                }

            } else {
                const field = this.fieldMapContext.fieldsMap.get(locationItemId.toString());
                field.select();
                field.fitView();
                this.selectedField = locationItemId;
                resolve(field);
            }
        });
    }

    async onFieldItemEdit(locationItem: LocationItem): Promise<void> {
        const field = await this.onFieldItemSelect(locationItem.location.id);
        if (field) {
            if (field.isEditable()) {
                field.edit();
                this.fieldMapContext.editOptionsSelectedValue = 'EDIT';
            } else {
                this.alertService.error('field-manager.errors.field.edit-blocked', null, 5000);
            }
        }
    }

    async onFieldItemDelete(locationItem: LocationItem): Promise<void> {
        const field = await this.onFieldItemSelect(locationItem.location.id);
        if (field) {
            if (field.isDeletable()) {
                field.delete();
            } else {
                this.alertService.error('field-manager.errors.field.delete-blocked', null, 5000);
            }
        }
    }

    onBlockItemSelect(locationItemId: number): FieldMapShape {
        if (this.fieldMapContext.blocksMap.has(locationItemId.toString())) {
            const block = this.fieldMapContext.blocksMap.get(locationItemId.toString());
            this.selectedBlock = locationItemId;
            block.select();
            block.fitView();
            return block;
        }
        return null;
    }

    onBlockItemEdit(locationItem: LocationItem): void {
        const block = this.onBlockItemSelect(locationItem.location.id);
        if (block) {
            if (block.isEditable()) {
                block.edit();
                this.fieldMapContext.editOptionsSelectedValue = 'EDIT';
            } else {
                this.alertService.error('field-manager.errors.block.edit-blocked', null, 5000);
            }
        }
    }

    onBlockItemDelete(locationItem: LocationItem): void {
        const block = this.onBlockItemSelect(locationItem.location.id);
        if (block) {
            if (block.isDeletable()) {
                block.delete();
            } else {
                this.alertService.error('field-manager.errors.block.delete-blocked', null, 5000);
            }

        }
    }

    deleteShape() {
        if (this.selectedFieldFieldMapShape) {
            this.selectedFieldFieldMapShape.delete();
        }
    }

    async unAssignStudy(fieldmapInfo: FieldmapInfo) {
        const loadedStudy = this.fieldMapContext.studiesMap.get(fieldmapInfo.instanceId.toString());
        if (loadedStudy) {
            loadedStudy.delete();
        } else {

            // Open confirmation modal and await user response
            const modalRef = this.modalService.open(ModalConfirmComponent);
            modalRef.componentInstance.title = this.translateService.instant('field-manager.modal.unassign-confirmation.title');
            modalRef.componentInstance.message = this.translateService.instant('field-manager.modal.unassign-confirmation.message', {
                param1: fieldmapInfo.studyName,
                param2: 'study'
            });

            const [error] = await customCatchError(modalRef.result);

            // If there's an error, the modal is dismissed/canceled.
            // In this case immediately reject the promise.
            if (error) {
                return;
            }

            try {
                const locationStudy = await this.locationStudyService.getByGeolocation(fieldmapInfo.instanceId).toPromise();
                if (locationStudy) {
                    await this.locationStudyService.deleteByLocationAndGeolocation(locationStudy.locId, locationStudy.ndGeolocationId).toPromise();
                }
                // Delete study geoJson data
                await this.fieldmapService.deleteGeoreferences(fieldmapInfo.crop, fieldmapInfo.instanceId.toString()).toPromise();
                this.studyInstancesView.refreshAll();
            } catch (error) {
                this.alertService.error('field-manager.errors.study.unassign-failed', null, 5000);
            }

        }
    }

    async loadStudy(fieldmapInfo: FieldmapInfo) {

        try {
            const existingLocationStudy = await this.locationStudyService.getByGeolocation(fieldmapInfo.instanceId).toPromise();
            if (!existingLocationStudy) {
                let study: Study;
                if (this.fieldMapContext.studiesMap.has(fieldmapInfo.instanceId.toString())) {
                    study = this.fieldMapContext.studiesMap.get(fieldmapInfo.instanceId.toString());
                } else {
                    study = await this.studyMapGenerator.loadStudyWithoutParentBlock(fieldmapInfo, this.map);
                }
                study.draw();
                study.select();
                study.fitView();
            } else {
                // Get the workbench location of the block linked to study.
                const blockLocation = await this.workbenchLocationService.getLocationById(existingLocationStudy.locId.toString()).toPromise();
                const fieldLocation = await this.workbenchLocationService.getLocationById(blockLocation.parentLocId.toString()).toPromise();
                const field = await this.studyMapGenerator.loadField(fieldLocation, this.map, this.drawnFields);
                // Once the field is generated
                if (this.fieldMapContext.studiesMap.has(fieldmapInfo.instanceId.toString())) {
                    const study = this.fieldMapContext.studiesMap.get(fieldmapInfo.instanceId.toString());
                    study.select();
                    study.fitView();
                    return study;
                } else {
                    return null;
                }
            }
        } catch (error) {
            this.alertService.error('field-manager.errors.study.load-failed', null, 5000);
        }
    }

    async editStudy(fieldmapInfo: FieldmapInfo) {
        const study = await this.loadStudy(fieldmapInfo);
        if (study) {
            // study.edit();
            study.resize();
        }
    }

    showSnackBar(type: 'ERROR' | 'INFO' | 'WARNING', message: string): void {
        this.snackBarMessage = this.translateService.instant(message);
        this.snackBarType = type;
    }

    hideSnackBar(): void {
        this.snackBarMessage = null;
        this.snackBarType = null;
    }

    toggleLeftSidenav() {
        this.leftSidenavOpen = !this.leftSidenavOpen;
    }

    toggleRightSidenav() {
        this.rightSidenavOpen = !this.rightSidenavOpen;
    }

    private setupSidenavListeners(): void {
        // Listen to left sidenav
        this.leftSideNav.openedChange.subscribe(() => {
            this.handleMapResize();
        });

        // Listen to right sidenav
        this.rightSideNav.openedChange.subscribe(() => {
            this.handleMapResize();
        });
    }

    private handleMapResize(): void {
        if (this.map) {
            // Add a small delay to ensure DOM is updated
            setTimeout(() => {
                this.map!.invalidateSize();
            }, 100);
        }
    }

    // Optional: Handle window resize
    @HostListener('window:resize')
    onWindowResize() {
        this.handleMapResize();
    }

    startThrobber(message: string) {
        this.isLoading = true;
        this.loadingMessage = message;
    }

    stopThrobber() {
        this.isLoading = false;
        this.loadingMessage = '';
    }

    isPolygonComplete(polygon) {
        const latlngs = polygon.getLatLngs()[0];
        return latlngs && latlngs.length >= 3;
    }

}
